import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Layers, Trash2, Plus, AlertCircle } from "lucide-react";
import { Controller, Control, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import { toast } from "@/components/ui/use-toast";
import Combobox from "@/components/ui/common/combobox";
import { PriceMaster } from "@/types/system-setting/priceMaster";

interface GroupBasedPricingProps {
  control: Control<PriceMaster>;
  errors: FieldErrors<PriceMaster>;
  watch: UseFormWatch<PriceMaster>;
  setValue: UseFormSetValue<PriceMaster>;
  readOnly?: boolean;
}

const GroupBasedPricing = ({ control, errors, watch, setValue, readOnly = false }: GroupBasedPricingProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  
  const isEnabled = watch("enable_group_pricing");
  const groupPricing = watch("group_pricing") || [];

  const { data: memberGroupData, isLoading: memberGroupLoading } = useGetCategoriesQuery(
    { query: "type=member", org_id: orgId },
    { skip: !orgId }
  );

  useEffect(() => {
    if (groupPricing.length > 0 && !isEnabled) {
      setValue("enable_group_pricing", true);
    }
  }, [groupPricing, isEnabled, setValue]);

  const memberGroupOptions = useMemo(() => {
    return memberGroupData && Array.isArray(memberGroupData.data)
      ? memberGroupData.data.map((group) => ({
          label: group.name,
          value: String(group.id),
        }))
      : [];
  }, [memberGroupData]);

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg shrink-0">
            <Layers className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">Group Based Pricing</h3>
            <p className="text-xs text-gray-600">Set different prices for specific member groups</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Enable</span>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                setValue("enable_group_pricing", checked);
                if (checked) {
                  if (groupPricing.length === 0) {
                    setValue("group_pricing", [{ group_id: 0, price: 0 }]);
                  }
                } else {
                  setValue("group_pricing", []);
                }
              }}
              disabled={readOnly}
            />
        </div>
      </div>

      {isEnabled && (
        <div className="space-y-4 mt-4">
          <Controller
            control={control}
            name="group_pricing"
            rules={{
              validate: {
                groupsValid: (groups) => {
                  if (!groups || groups.length === 0) return true;
                  if (groups.length > 7) return "Maximum 7 group rules allowed";
                  for (let i = 0; i < groups.length; i++) {
                    if (!groups[i].group_id) return `Group Rule ${i + 1}: Member Group is required`;
                    if (groups[i].price <= 0) return `Group Rule ${i + 1}: Price should be greater than zero`;
                  }
                  const groupIds = groups.map((g) => g.group_id).filter(id => id !== 0);
                  if (groupIds.length !== new Set(groupIds).size) return "Duplicate groups are not allowed";
                  return true;
                },
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div className="space-y-4">
                {error && <p className="text-red-500 text-sm">{error.message}</p>}
                {field.value?.map((rule, index) => (
                  <Card key={index} className="p-4 bg-white border-2 border-blue-100 hover:border-blue-200 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-xs text-blue-700 border-blue-300">
                        Group Rule {index + 1}
                      </Badge>
                      {(field.value?.length || 0) > 1 && !readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newRules = [...(field.value || [])];
                            newRules.splice(index, 1);
                            field.onChange(newRules);
                          }}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Member Group <span className="text-red-500">*</span>
                        </label>
                        <Combobox
                          list={memberGroupOptions}
                          defaultValue={memberGroupOptions?.find((g) => g.value === String(rule.group_id))?.label || ""}
                          setFilter={(selectedLabel: string) => {
                            const selectedOption = memberGroupOptions?.find((option) => option.label === selectedLabel);
                            const newRules = [...(field.value || [])];
                            if (selectedOption) {
                              const isDuplicate = newRules.some((r, i) => i !== index && r.group_id === Number(selectedOption.value));
                              if (isDuplicate) {
                                toast({ variant: "destructive", description: "This group is already selected." });
                                return;
                              }
                              newRules[index].group_id = Number(selectedOption.value);
                            } else {
                              newRules[index].group_id = 0;
                            }
                            field.onChange(newRules);
                          }}
                          label="Member Group"
                          showLabel={false}
                          disabled={memberGroupLoading || readOnly}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Price <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <p className="absolute left-3 top-[45%] transform -translate-y-1/2 h-4 w-4 text-gray-500 font-medium">Rs.</p>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={rule.price}
                            className="pl-10"
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              const newRules = [...(field.value || [])];
                              newRules[index].price = isNaN(value) ? 0 : value;
                              field.onChange(newRules);
                            }}
                            placeholder="0.00"
                            disabled={readOnly}
                          />
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {!readOnly && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-4 border-t border-blue-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        type="button"
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400"
                        onClick={() => {
                          if ((field.value?.length || 0) < 7) {
                            field.onChange([...(field.value || []), { group_id: 0, price: 0 }]);
                          } else {
                            toast({ variant: "destructive", description: "Maximum 7 group rules allowed" });
                          }
                        }}
                        disabled={memberGroupLoading || (field.value?.length || 0) >= 7}
                      >
                        <Plus className="h-4 w-4 mr-2" /> Add Group Rule
                      </Button>
                      <div className="text-xs text-gray-500">{field.value?.length || 0}/7 rules</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default GroupBasedPricing;
